import React, { type FC } from 'react';
import { defineMessages, FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { type GlyphProps } from '@atlaskit/icon';
import WorldIcon from '@atlaskit/icon/glyph/world';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { LockIcon } from '../svg/LockIcon-svg';
import { UnlockIcon } from '../svg/UnlockIcon-svg';

export const i18n = defineMessages({
	share: {
		id: 'share-and-restrict-dialog.controlled-share-button.share',
		defaultMessage: 'Share',
		description:
			'Button text for the Share and Restrict button, which opens the Share and Restrict dialog',
	},
});

export type ControlledShareButtonIcon = 'open' | 'restricted' | 'public' | 'error';

export type ControlledShareButtonProps = {
	isBlue: boolean;
	icon: ControlledShareButtonIcon;
	onClick: () => void;
	tooltipMessage: MessageDescriptor;
};

const ICON_PROP_TO_ICON_COMPONENT: {
	[key in ControlledShareButtonIcon]:
		| typeof UnlockIcon
		| typeof LockIcon
		| typeof WorldIcon
		| typeof WarningIcon;
} = {
	open: UnlockIcon,
	restricted: LockIcon,
	public: WorldIcon,
	error: WarningIcon,
};

const makeIconProps = (icon: ControlledShareButtonIcon, isBlue: boolean): Partial<GlyphProps> => {
	switch (icon) {
		case 'error':
			return { primaryColor: token('color.icon.warning') };
		case 'restricted':
			return isBlue ? {} : { primaryColor: token('color.icon.danger') };
		default:
			return {};
	}
};

export const ControlledShareButton: FC<ControlledShareButtonProps> = ({
	isBlue,
	icon,
	onClick,
	tooltipMessage,
}) => {
	const { formatMessage } = useIntl();
	const Icon = ICON_PROP_TO_ICON_COMPONENT[icon];
	const iconProps: GlyphProps = {
		label: '',
		...makeIconProps(icon, isBlue),
	};

	return (
		<Tooltip content={formatMessage(tooltipMessage)} position="bottom">
			{(tooltipProps) => (
				<Button
					// TODO: Add a11y label per https://product-fabric.atlassian.net/browse/USD-81
					{...tooltipProps}
					appearance={isBlue ? 'primary' : 'default'}
					iconBefore={(parentProps) => <Icon {...parentProps} {...iconProps} />}
					onClick={onClick}
					aria-label={`${formatMessage(i18n.share)}, ${formatMessage(tooltipMessage)}`}
				>
					<FormattedMessage {...i18n.share} />
				</Button>
			)}
		</Tooltip>
	);
};
